import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { ProviderReference } from '../../../../../js/api';
import { ProviderReferencesService } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { FieldValidationErrorMessage } from '../../../components/field-validation-error-message';
import { Description, Field, Fieldset, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { Textarea } from '../../../components/textarea';
import { dateAtLocalHour, isLocalToday } from '../../../utils/date';
import { STAFF_PROVIDER_QUERY_KEY, STAFF_PROVIDER_REFERENCES_QUERY_KEY } from './queryKeys';
import 'react-datepicker/dist/react-datepicker.css';

/** Staff may schedule the survey request from today up to this many months out. */
export const REQUEST_SCHEDULE_MAX_MONTHS = 3;
/** A date-only choice becomes a send time the morning of that day (local time). */
const REQUEST_SEND_HOUR = 8;

export const requestScheduleBounds = () => {
  const min = new Date();
  min.setHours(0, 0, 0, 0);
  const max = new Date(min);
  max.setMonth(max.getMonth() + REQUEST_SCHEDULE_MAX_MONTHS);
  return { min, max };
};

/** Today sends right away; any other day goes out that morning via the hourly job. */
export const toRequestScheduledAt = (date: Date): string =>
  (isLocalToday(date) ? new Date() : dateAtLocalHour(date, REQUEST_SEND_HOUR)).toISOString();

const schema = z.object({
  full_name: z.string().trim().min(1, 'Name is required').max(100),
  title: z.string().trim().max(100).optional(),
  email: z.string().trim().email('Enter a valid email'),
  phone_number: z.string().trim().optional(),
  address: z.string().trim().optional(),
  request_scheduled_at: z.date({ required_error: 'Pick when the request should be sent' }),
});
type FormValues = z.infer<typeof schema>;

type ReferenceFormDialogProps = {
  open: boolean;
  onClose: () => void;
  providerId: string;
  /** Editing an existing reference; omitted for a new one. */
  reference?: ProviderReference | null;
};

/**
 * Enter a reference on the provider's behalf (name, contact, and the day the survey
 * request goes out — today by default, at most three months out) or edit its contact
 * details. The email is locked once a request has reached the reference
 * (`can_edit_email` from the backend rule).
 */
export function ReferenceFormDialog({
  open,
  onClose,
  providerId,
  reference,
}: ReferenceFormDialogProps) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(reference);
  const emailLocked = isEdit && !reference?.can_edit_email;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: '',
      title: '',
      email: '',
      phone_number: '',
      address: '',
      request_scheduled_at: new Date(),
    },
  });
  const { control, register, handleSubmit, reset, setError, formState } = form;
  const { min: minDate, max: maxDate } = requestScheduleBounds();

  useEffect(() => {
    if (!open) return;
    reset({
      full_name: reference?.full_name ?? '',
      title: reference?.title ?? '',
      email: reference?.email ?? '',
      phone_number: reference?.phone_number ?? '',
      address: reference?.address ?? '',
      request_scheduled_at: new Date(),
    });
  }, [open, reference, reset]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_REFERENCES_QUERY_KEY, providerId] });
    queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_QUERY_KEY, providerId] });
  };

  const applyFieldErrors = (error: unknown) => {
    const body = (error as { body?: Record<string, unknown> })?.body;
    if (body && typeof body === 'object') {
      Object.entries(body).forEach(([field, messages]) => {
        if (field in schema.shape || field === 'request_scheduled_at') {
          setError(field as keyof FormValues, {
            message: Array.isArray(messages) ? String(messages[0]) : String(messages),
          });
        }
      });
    }
  };

  const createMutation = useMutation({
    mutationFn: (values: FormValues) =>
      ProviderReferencesService.providerReferencesCreate({
        requestBody: {
          provider: providerId,
          full_name: values.full_name,
          title: values.title || '',
          email: values.email,
          phone_number: values.phone_number || null,
          address: values.address || '',
          request_scheduled_at: toRequestScheduledAt(values.request_scheduled_at),
        },
      }),
    onSuccess: () => {
      toast.success('Reference added');
      invalidate();
      onClose();
    },
    onError: (error: Error) => {
      applyFieldErrors(error);
      handleApiError(error, { messagePrefix: 'Error adding reference' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: FormValues) =>
      ProviderReferencesService.providerReferencesPartialUpdate({
        id: reference!.id,
        requestBody: {
          full_name: values.full_name,
          title: values.title || '',
          phone_number: values.phone_number || null,
          address: values.address || '',
          // only send the email when it may change; the backend rejects it otherwise
          ...(emailLocked ? {} : { email: values.email }),
        },
      }),
    onSuccess: () => {
      toast.success('Reference updated');
      invalidate();
      onClose();
    },
    onError: (error: Error) => {
      applyFieldErrors(error);
      handleApiError(error, { messagePrefix: 'Error updating reference' });
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const onSubmit = (values: FormValues) =>
    isEdit ? updateMutation.mutate(values) : createMutation.mutate(values);

  return (
    <Dialog open={open} onClose={onClose} size="xl">
      <DialogTitle className="tw:!text-2xl tw:!text-brand-green-0 tw:!mt-0 tw:!pt-0">
        {isEdit ? 'Edit Reference' : 'Add Reference'}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="reference-form">
        <DialogBody>
          <Fieldset>
            <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-3">
              <Field>
                <Label>
                  Full Name <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <Input {...register('full_name')} invalid={!!formState.errors.full_name} />
                {formState.errors.full_name && (
                  <FieldValidationErrorMessage message={formState.errors.full_name.message} />
                )}
              </Field>
              <Field>
                <Label>
                  Email <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <Input
                  type="email"
                  {...register('email')}
                  disabled={emailLocked}
                  invalid={!!formState.errors.email}
                />
                {emailLocked && (
                  <Description>
                    The request has already reached this address, so it can no longer change.
                  </Description>
                )}
                {formState.errors.email && (
                  <FieldValidationErrorMessage message={formState.errors.email.message} />
                )}
              </Field>
              <Field>
                <Label>Title</Label>
                <Input {...register('title')} />
              </Field>
              <Field>
                <Label>Phone</Label>
                <Input {...register('phone_number')} invalid={!!formState.errors.phone_number} />
                {formState.errors.phone_number && (
                  <FieldValidationErrorMessage message={formState.errors.phone_number.message} />
                )}
              </Field>
            </div>
            <Field className="tw:mt-3">
              <Label>Address</Label>
              <Textarea rows={2} {...register('address')} />
            </Field>

            {!isEdit && (
              <Field className="tw:mt-4">
                <Label>
                  Send Request On <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <Controller
                  name="request_scheduled_at"
                  control={control}
                  render={({ field }) => (
                    <ReactDatePicker
                      className="datepicker-input"
                      wrapperClassName="datepicker-wrapper"
                      selected={field.value ?? null}
                      onChange={(date) => field.onChange(date)}
                      minDate={minDate}
                      maxDate={maxDate}
                      placeholderText="Select a date"
                      dateFormat="MM/dd/yyyy"
                      disabled={isPending}
                    />
                  )}
                />
                <Description>
                  Today sends the survey right away; a later date goes out that morning.
                </Description>
                {formState.errors.request_scheduled_at && (
                  <FieldValidationErrorMessage
                    message={formState.errors.request_scheduled_at.message}
                  />
                )}
              </Field>
            )}
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="zinc-outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" color="brandgreen1" disabled={isPending}>
            {isEdit ? 'Save' : 'Add Reference'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
